// Logotipo customizado (Administração > Identidade Visual > Logotipos),
// extraído de app-sidebar.tsx pra ser reaproveitado também pela casca da
// Agenda (agenda-shell.tsx) sem duplicar a lógica de pré-carregamento.
import { useEffect, useState } from "react";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import { useAdminBranding } from "@/hooks/queries";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";

// Logotipo "sumindo e reaparecendo" no F5 (bug relatado 2026-08-28): o
// <img> trocava de src pro logotipo customizado assim que useAdminBranding()
// respondia, mas o navegador ainda não tinha os bytes da imagem nova —
// ficava em branco até baixar. Pré-carrega (Image().onload) antes de trocar
// o que é exibido, e guarda a última URL confirmada em localStorage pra já
// abrir certo no próximo F5, sem esperar a rede. Começa com o asset padrão
// embutido (sempre disponível, sem rede) até o cache/fetch resolverem —
// nunca em branco.
const BRANDING_LOGO_CACHE_KEY = "gpsdrive:branding-logo-url";

// `dark`: tela com fundo escuro — usa o "Logotipo para fundo escuro" quando
// a empresa enviou um (pedido 2026-10-08), senão o logotipo normal.
export function useBrandLogo(dark = false) {
  const { data: branding } = useAdminBranding();
  const [displayedLogoUrl, setDisplayedLogoUrl] = useState<string>(() => {
    try {
      return localStorage.getItem(BRANDING_LOGO_CACHE_KEY) || logoAsset.url;
    } catch {
      return logoAsset.url;
    }
  });

  useEffect(() => {
    if (branding === undefined) return; // ainda carregando — mantém o que já está na tela
    const nextUrl = branding.logoUrl ? resolveGpsDriveUrl(branding.logoUrl) : logoAsset.url;
    if (nextUrl === logoAsset.url) {
      setDisplayedLogoUrl(logoAsset.url);
      try {
        localStorage.removeItem(BRANDING_LOGO_CACHE_KEY);
      } catch {
        // ignora — pior caso é um flash no próximo F5
      }
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setDisplayedLogoUrl(nextUrl);
      try {
        localStorage.setItem(BRANDING_LOGO_CACHE_KEY, nextUrl);
      } catch {
        // localStorage indisponível/cheio — sem cache, mas sem quebrar o fluxo
      }
    };
    img.src = nextUrl;
    return () => {
      cancelled = true;
    };
  }, [branding]);

  const darkLogoUrl = dark && branding?.logoDarkUrl ? resolveGpsDriveUrl(branding.logoDarkUrl) : null;
  return { branding, displayedLogoUrl: darkLogoUrl ?? displayedLogoUrl, defaultLogoUrl: logoAsset.url };
}
